/**
 * ModalDialog — the migration target for hand-rolled `fixed inset-0` modals
 * (SH-5).
 *
 * A hand-rolled overlay has no focus trap, does not restore focus to the
 * control that opened it and ignores Escape (ACCESSIBILITY_MATRIX.md: "Dialogs
 * contain and restore focus, close with Escape"). ModalDialog is Radix Dialog
 * underneath, but keeps the caller's own panel markup and classes, so an
 * existing modal migrates by swapping its two wrapper divs:
 *
 *   {open && (
 *     <ModalDialog title="Edit template" onClose={() => setOpen(false)}
 *       className="w-full max-w-lg rounded-xl bg-... p-6">
 *       ...existing panel children...
 *     </ModalDialog>
 *   )}
 *
 * `title` is the dialog's accessible name (rendered visually hidden; keep the
 * visible heading in the panel). Pass `dismissible={false}` while a mutation
 * is in flight to ignore Escape and outside clicks. `placement="right"` keeps
 * a right-hand sheet layout; prefer Drawer for new side panels.
 */
import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { useReturnFocusOnClose } from './returnFocus';

export interface ModalDialogProps {
  /** Whether the dialog is open (default true, for `{show && <ModalDialog/>}`). */
  open?: boolean;
  /** Called on Escape, outside click, or any DialogClose inside the panel. */
  onClose: () => void;
  /** Accessible name for the dialog (visually hidden). */
  title: string;
  /** Optional accessible description (visually hidden). */
  description?: string;
  /** Classes for the panel (the old inner `<div>`). */
  className?: string;
  /** Classes for the backdrop (default: dimmed + blurred). */
  overlayClassName?: string;
  /** Where the panel sits (default centre). */
  placement?: 'center' | 'right' | 'top';
  /** When false, Escape and outside clicks do not close the dialog. */
  dismissible?: boolean;
  /** Stacking order for dialogs opened above other overlays. */
  zIndexClassName?: string;
  children: ReactNode;
}

const PLACEMENT = {
  center: 'items-center justify-center p-4',
  right: 'items-stretch justify-end',
  top: 'items-start justify-center p-4 pt-[10vh]',
} as const;

export function ModalDialog({
  open = true,
  onClose,
  title,
  description,
  className,
  overlayClassName = 'bg-black/50 backdrop-blur-sm',
  placement = 'center',
  dismissible = true,
  zIndexClassName = 'z-50',
  children,
}: ModalDialogProps) {
  // Radix returns focus to a Dialog.Trigger; these modals are opened by plain
  // buttons, so return focus to whatever had it when the dialog opened.
  const returnFocus = useReturnFocusOnClose();

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && dismissible) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="modal-dialog-overlay"
          className={clsx('fixed inset-0', zIndexClassName, overlayClassName)}
        />
        <div
          className={clsx(
            'pointer-events-none fixed inset-0 flex overflow-y-auto',
            zIndexClassName,
            PLACEMENT[placement]
          )}
        >
          <DialogPrimitive.Content
            {...(description ? {} : { 'aria-describedby': undefined })}
            className={clsx('pointer-events-auto relative focus:outline-none', className)}
            onEscapeKeyDown={(event) => {
              if (!dismissible) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (!dismissible) event.preventDefault();
            }}
            {...returnFocus}
          >
            {/* Names the dialog via aria-labelledby without a second visible or
                heading-role copy of the panel's own heading. */}
            <DialogPrimitive.Title asChild>
              <span hidden>{title}</span>
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="sr-only">{description}</DialogPrimitive.Description>
            )}
            {children}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Closes the surrounding ModalDialog (wrap an existing button with `asChild`). */
export const ModalDialogClose = DialogPrimitive.Close;
